import React, { useMemo, useState, useCallback } from 'react';
import {
  CalendarClock, AlertCircle, RotateCcw, Clock, Repeat, CheckCircle2, ChevronRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from './ui/sheet';
import { toast } from 'sonner';
import { useTasks } from '../context/TasksContext';
import { useLeads } from '../context/LeadsContext';
import RescheduleJobModal from './scheduler/RescheduleJobModal';

/**
 * Sprint 3 — Scheduling Attention tile (Dashboard).
 *
 * Architectural notes (per the audit):
 *   - PERMANENT tile (renders even when all three counters are 0).
 *   - Counts derive from the existing `tasks` + `leads` contexts. No new
 *     collections, endpoints, or scheduling logic.
 *   - Per-row Reschedule action goes through the canonical Sprint 1 helper
 *     (RescheduleJobModal → rescheduleTask) — same history[] + cursor sync.
 *   - "Mark complete" uses the existing updateTask path (already idempotent).
 *   - Recurring slip rows just deep-link to the customer profile; the Service
 *     Plan card there is the canonical place to re-anchor the cursor.
 *
 * Three buckets:
 *   • Overdue        — type=job & scheduledStart < todayStart & status ∉ closed
 *   • Stalled        — status ∈ {on_site, en_route} & scheduledStart > 4h old
 *   • Recurring Issues — lead.recurrence.active & nextJobDate < today & no
 *                       live future job task on the calendar for that lead
 */

const STALLED_THRESHOLD_MS = 4 * 60 * 60 * 1000; // 4 hours

const SEGMENTS = {
  overdue: {
    label:     'Overdue',
    plural:    'Overdue visits',
    icon:      Clock,
    iconClass: 'text-red-600',
    bgClass:   'bg-red-50',
    emptyText: 'No overdue visits.',
    description: 'Visits scheduled in the past that are still open. Reschedule or mark them complete.',
  },
  stalled: {
    label:     'Stalled',
    plural:    'Stalled visits',
    icon:      AlertCircle,
    iconClass: 'text-amber-600',
    bgClass:   'bg-amber-50',
    emptyText: 'No stalled visits.',
    description: 'Visits marked en-route or on-site for longer than four hours. Confirm completion or reschedule.',
  },
  recurring: {
    label:     'Recurring Issues',
    plural:    'Recurring cursor slips',
    icon:      Repeat,
    iconClass: 'text-violet-600',
    bgClass:   'bg-violet-50',
    emptyText: 'All recurring plans are on track.',
    description: "Recurring customers whose next-visit cursor has slipped past today with no live task on the calendar. Open the profile to book the next visit.",
  },
};

// ── Helpers ────────────────────────────────────────────────────────────────

const safeStart = (task) => {
  if (!task?.scheduledStart) return null;
  const d = new Date(task.scheduledStart);
  return Number.isNaN(d.getTime()) ? null : d;
};

const ymdLocal = (date) => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const parseRecurrenceCursor = (cursor) => {
  if (!cursor) return null;
  if (cursor?.toDate) {
    try { return cursor.toDate(); } catch { /* noop */ }
  }
  // Accept YYYY-MM-DD by anchoring to local midnight.
  if (typeof cursor === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(cursor)) {
    const [y, m, d] = cursor.split('-').map((v) => parseInt(v, 10));
    return new Date(y, m - 1, d, 0, 0, 0, 0);
  }
  const d = new Date(cursor);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatRelativePast = (date) => {
  if (!date) return '';
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) return 'upcoming';
  const mins = Math.round(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'yesterday';
  if (days < 7)  return `${days} days ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

// ── Drill-down sheet ────────────────────────────────────────────────────────

const DrilldownSheet = ({ segment, items, leadMap, onClose, onReschedule, onMarkComplete, onOpenProfile }) => {
  const cfg = segment ? SEGMENTS[segment] : null;
  const open = !!segment;
  if (!cfg) return null;
  const Icon = cfg.icon;

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col"
        data-testid={`scheduling-attention-sheet-${segment}`}
      >
        <SheetHeader className="px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className={`h-9 w-9 rounded-full ${cfg.bgClass} flex items-center justify-center shrink-0`}>
              <Icon className={`h-4 w-4 ${cfg.iconClass}`} />
            </div>
            <div className="text-left flex-1 min-w-0">
              <SheetTitle className="text-base font-semibold leading-tight">
                {cfg.plural}
              </SheetTitle>
              <SheetDescription className="text-xs text-gray-500">
                {cfg.description}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div
          className="flex-1 overflow-y-auto"
          data-testid={`scheduling-attention-list-${segment}`}
        >
          {items.length === 0 ? (
            <div className="px-5 py-10 text-center" data-testid={`scheduling-attention-empty-${segment}`}>
              <CheckCircle2 className="h-10 w-10 text-emerald-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">{cfg.emptyText}</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {items.map((row) => {
                const lead = leadMap[row.leadId] || null;
                const customerName = (lead?.fullName || lead?.name || row.clientName || 'Customer').trim();
                return (
                  <li
                    key={row.id}
                    className="px-5 py-3 hover:bg-gray-50"
                    data-testid={`scheduling-attention-row-${row.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={() => onOpenProfile(row.leadId)}
                        className="flex-1 min-w-0 text-left"
                        data-testid={`scheduling-attention-row-profile-${row.id}`}
                      >
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {customerName}
                        </p>
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          {row.subtitle}
                        </p>
                      </button>
                      <ChevronRight className="h-4 w-4 text-gray-300 shrink-0" />
                    </div>
                    {(segment === 'overdue' || segment === 'stalled') && (
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          type="button"
                          onClick={() => onReschedule(row)}
                          className="text-[11px] font-medium px-2 py-1 rounded-md border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 transition-colors inline-flex items-center gap-1"
                          data-testid={`scheduling-attention-reschedule-${row.id}`}
                        >
                          <RotateCcw className="h-3 w-3" />
                          Reschedule
                        </button>
                        <button
                          type="button"
                          onClick={() => onMarkComplete(row)}
                          className="text-[11px] font-medium px-2 py-1 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors inline-flex items-center gap-1"
                          data-testid={`scheduling-attention-complete-${row.id}`}
                        >
                          <CheckCircle2 className="h-3 w-3" />
                          Mark complete
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ── Tile (main) ─────────────────────────────────────────────────────────────

// Lifted out of the tile so it isn't redefined on every render — eslint
// react/no-unstable-nested-components would otherwise complain and the
// underlying DOM nodes would be destroyed/re-mounted on every state change.
const Segment = ({ id, value, colorClass, onOpen }) => {
  const cfg = SEGMENTS[id];
  return (
    <button
      type="button"
      onClick={() => onOpen(id)}
      className={`inline-flex items-center gap-1.5 px-1.5 -mx-1.5 py-0.5 rounded-md hover:bg-white/60 transition-colors ${value === 0 ? 'cursor-default' : ''}`}
      disabled={value === 0}
      title={value === 0 ? `No ${cfg.label.toLowerCase()} items` : `View ${value} ${value === 1 ? cfg.label.toLowerCase() : cfg.plural.toLowerCase()}`}
      data-testid={`scheduling-attention-${id}-btn`}
    >
      <span className={`font-bold ${colorClass}`} data-testid={`scheduling-attention-${id}-count`}>
        {value}
      </span>
      <span className="text-gray-700">{cfg.label}</span>
    </button>
  );
};

const SchedulingAttentionTile = () => {
  const navigate = useNavigate();
  const { tasks, completeJob } = useTasks();
  const { leads } = useLeads();
  const [openSegment, setOpenSegment] = useState(null);
  const [rescheduleTask, setRescheduleTask] = useState(null);

  const leadMap = useMemo(() => {
    const map = {};
    leads.forEach((l) => { map[l.id] = l; });
    return map;
  }, [leads]);

  // ── Derive the three buckets ──────────────────────────────────────────
  const { overdue, stalled, recurring } = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const todayKey   = ymdLocal(todayStart);

    const overdueOut  = [];
    const stalledOut  = [];
    const closed = new Set(['completed', 'cancelled', 'archived']);

    // Build a leadId → "has a live future job?" lookup for the recurring check.
    const leadHasFutureJob = new Set();
    tasks.forEach((t) => {
      if (t.type !== 'job') return;
      const s = safeStart(t);
      if (!s) return;
      if (s >= todayStart && !closed.has(t.status)) {
        if (t.leadId) leadHasFutureJob.add(t.leadId);
      }
    });

    tasks.forEach((t) => {
      if (t.type !== 'job') return;
      if (closed.has(t.status)) return;
      const start = safeStart(t);
      if (!start) return;

      // Overdue: scheduledStart was strictly before today's midnight.
      if (start < todayStart) {
        overdueOut.push({
          id: t.id,
          taskId: t.id,
          leadId: t.leadId,
          clientName: t.clientName,
          subtitle: `Scheduled ${formatRelativePast(start)} · status: ${t.status || 'scheduled'}`,
          when: start,
          task: t,
        });
        return; // an overdue task isn't double-counted as stalled.
      }

      // Stalled: in flight (en_route / on_site) for > 4h since scheduledStart.
      if ((t.status === 'en_route' || t.status === 'on_site') &&
          now.getTime() - start.getTime() > STALLED_THRESHOLD_MS) {
        stalledOut.push({
          id: t.id,
          taskId: t.id,
          leadId: t.leadId,
          clientName: t.clientName,
          subtitle: `${t.status === 'on_site' ? 'On-site' : 'En route'} since ${start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })} · ${formatRelativePast(start)}`,
          when: start,
          task: t,
        });
      }
    });

    // Recurring Issues: lead.recurrence.active && nextJobDate < today && no
    // live future task on the calendar for that lead.
    const recurringOut = [];
    leads.forEach((l) => {
      if (!l?.recurrence || l.recurrence.active !== true) return;
      const cursor = parseRecurrenceCursor(l.recurrence.nextJobDate);
      if (!cursor) return;
      if (cursor >= todayStart) return;        // not slipped
      if (leadHasFutureJob.has(l.id)) return;  // cursor stale only — there IS a task ahead
      recurringOut.push({
        id: `lead-${l.id}`,
        leadId: l.id,
        clientName: l.fullName || l.name,
        subtitle: `Next visit cursor was ${cursor.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} · ${formatRelativePast(cursor)}`,
        when: cursor,
      });
    });

    // Sort each bucket by oldest first (worst-first).
    const byWhenAsc = (a, b) => (a.when?.getTime?.() || 0) - (b.when?.getTime?.() || 0);
    overdueOut.sort(byWhenAsc);
    stalledOut.sort(byWhenAsc);
    recurringOut.sort(byWhenAsc);

    return { overdue: overdueOut, stalled: stalledOut, recurring: recurringOut };
  }, [tasks, leads]);

  const total = overdue.length + stalled.length + recurring.length;
  const healthy = total === 0;

  const itemsFor = useCallback((segment) => {
    if (segment === 'overdue')   return overdue;
    if (segment === 'stalled')   return stalled;
    if (segment === 'recurring') return recurring;
    return [];
  }, [overdue, stalled, recurring]);

  // ── Row actions (reuse canonical contracts) ───────────────────────────
  const handleReschedule = useCallback((row) => {
    if (!row?.task) return;
    setOpenSegment(null);
    setRescheduleTask(row.task);
  }, []);

  const handleMarkComplete = useCallback(async (row) => {
    if (!row?.taskId) return;
    try {
      // Sprint 4 — single completion contract: route through completeJob
      // (backend) so the recurrence engine runs even on stale / overdue
      // visits. This is the very drift Schedule Attention is designed
      // to surface; using updateTask here would just spawn more of it.
      await completeJob(row.taskId, { quoteId: row.task?.quoteId });
      toast.success(`Marked ${row.clientName || 'visit'} complete`);
    } catch (err) {
      console.error('[SchedulingAttention] mark complete failed', err);
      toast.error('Could not mark complete', {
        description: err?.message || 'Please retry — backend completion is required.',
      });
    }
  }, [completeJob]);

  const handleOpenProfile = useCallback((leadId) => {
    if (!leadId) return;
    setOpenSegment(null);
    navigate(`/clients/${leadId}`);
  }, [navigate]);

  return (
    <>
      <div
        className={`hidden lg:flex w-full items-center gap-4 rounded-lg px-4 py-3 shadow-sm border transition-colors ${
          healthy
            ? 'bg-gradient-to-r from-emerald-50 to-white border-emerald-100'
            : 'bg-gradient-to-r from-red-50 to-white border-red-200'
        }`}
        data-testid="scheduling-attention-tile"
      >
        <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${healthy ? 'bg-emerald-100' : 'bg-red-100'}`}>
          {healthy
            ? <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            : <CalendarClock className="h-5 w-5 text-red-600" />
          }
        </div>

        <div className="flex-1 min-w-0">
          <p className={`text-[11px] font-semibold uppercase tracking-wider ${healthy ? 'text-emerald-700' : 'text-red-700'}`}>
            Schedule Attention
            <span className="ml-2 normal-case font-normal tracking-normal text-gray-400">
              {healthy ? '· Everything is on schedule' : `· ${total} item${total === 1 ? '' : 's'} to review`}
            </span>
          </p>
          <p className="text-sm mt-0.5 leading-snug" data-testid="scheduling-attention-segments">
            <Segment id="overdue"   value={overdue.length}   colorClass={overdue.length   ? 'text-red-700'    : 'text-gray-400'} onOpen={setOpenSegment} />
            <span className="text-gray-300 mx-1.5">·</span>
            <Segment id="stalled"   value={stalled.length}   colorClass={stalled.length   ? 'text-amber-700'  : 'text-gray-400'} onOpen={setOpenSegment} />
            <span className="text-gray-300 mx-1.5">·</span>
            <Segment id="recurring" value={recurring.length} colorClass={recurring.length ? 'text-violet-700' : 'text-gray-400'} onOpen={setOpenSegment} />
          </p>
        </div>
      </div>

      {/* Mobile companion strip — single line, taps open the same drill-down */}
      <div
        className={`lg:hidden flex items-stretch w-full rounded-lg overflow-hidden border ${
          healthy ? 'border-emerald-200' : 'border-red-200'
        }`}
        data-testid="scheduling-attention-tile-mobile"
      >
        <div className={`flex items-center justify-center px-3 ${healthy ? 'bg-emerald-50' : 'bg-red-50'}`}>
          {healthy
            ? <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            : <CalendarClock className="h-5 w-5 text-red-600" />
          }
        </div>
        <div className="flex-1 bg-white">
          <p className="px-3 pt-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            Schedule Attention
          </p>
          <div className="grid grid-cols-3 px-1 pb-2">
            {[
              { id: 'overdue',   value: overdue.length,   color: 'text-red-700' },
              { id: 'stalled',   value: stalled.length,   color: 'text-amber-700' },
              { id: 'recurring', value: recurring.length, color: 'text-violet-700' },
            ].map(({ id, value, color }) => (
              <button
                key={id}
                type="button"
                onClick={() => setOpenSegment(id)}
                disabled={value === 0}
                className="flex flex-col items-center justify-center py-1 active:opacity-70 disabled:opacity-100"
                data-testid={`scheduling-attention-${id}-btn-mobile`}
              >
                <span className={`text-xl font-bold leading-none ${value === 0 ? 'text-gray-300' : color}`}>{value}</span>
                <span className="text-[10px] text-gray-500 mt-0.5">{SEGMENTS[id].label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <DrilldownSheet
        segment={openSegment}
        items={itemsFor(openSegment)}
        leadMap={leadMap}
        onClose={() => setOpenSegment(null)}
        onReschedule={handleReschedule}
        onMarkComplete={handleMarkComplete}
        onOpenProfile={handleOpenProfile}
      />

      <RescheduleJobModal
        task={rescheduleTask}
        isOpen={!!rescheduleTask}
        onClose={() => setRescheduleTask(null)}
      />
    </>
  );
};

export default SchedulingAttentionTile;
